import { Box, Flex } from 'reflexbox';
import Delta from 'quill-delta';
import styled from 'styled-components';
import moment from 'moment';
import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { JunkIcon, PencilIcon } from '@treams/treams-icons';
import { Link } from '@treams/treams-routes';
import { Avatar, Button, Editable, Paragraph, Spinner } from '@treams/treams-ui';

import {
	Comment as CommentType,
	User,
	useArchiveCommentMutation,
	useCommentMutation,
} from 'generated/graphql';
import { CommentSkeleton } from '.';

type CommentView = Pick<
	CommentType,
	'id' | 'content' | 'createdAt' | 'isArchived' | 'viewerCanUpdate'
> & {
	user: ComponentProps<typeof Avatar>['user'] & Pick<User, 'id'>;
};

type Props = {
	comment: CommentView;
	onDelete?: () => void;
	onRefresh?: () => void;
};

export default function Comment({ comment, onDelete, onRefresh }: Props) {
	const { t } = useTranslation();

	const [isActive] = useState(false);

	const [archive, commentArchiveResponse] = useArchiveCommentMutation({
		variables: {
			input: {
				id: comment.id,
			},
		},
	});

	const [update] = useCommentMutation();

	const commentValue = comment.isArchived
		? new Delta().insert(t('comments.comment.content.removed'))
		: comment.content;

	const onArchiveClick = async () => {
		await archive();
		onDelete?.();
		onRefresh?.();
	};

	const onContentChange = (value: Delta) => {
		update({
			variables: {
				input: {
					id: comment.id,
					content: value,
				},
			},
		});
	};

	const buttonProps: ComponentProps<typeof Button> = {
		icon: <PencilIcon />,
		type: 'secondary',
		title: undefined,
	};

	return (
		<Editable.Quill.Button
			active={isActive}
			onChange={onContentChange}
			value={commentValue}
			buttonProps={buttonProps}
		>
			{(value, { button, active }) => (
				<Flex alignItems="center">
					<Flex minWidth="100%" mr={1}>
						<Box flexGrow={0} flexShrink={0}>
							<Link to={`/users/${comment.user.id}`}>
								<Avatar user={comment.user} size={32} />
							</Link>
						</Box>
						<Box flexGrow={1} flexShrink={1} ml={2}>
							<Flex mb={2} ml={2}>
								<Paragraph flexGrow={1} flexShrink={1}>
									<Link to={`/users/${comment.user.id}`}>
										{comment.user.firstName} {comment.user.lastName}
									</Link>
								</Paragraph>
								<Paragraph flexGrow={0} flexShrink={0} ml={2}>
									{moment(comment.createdAt).format('LLL')}
								</Paragraph>
							</Flex>
							<Flex alignItems="center" justifyContent="center">
								<Container p={2} flexGrow={1} flexShrink={1} minWidth="100%">
									<Paragraph>
										{/* Internally value is put in a Quill-text */}
										{comment.isArchived ? <Box opacity={0.5}>{value}</Box> : value}
									</Paragraph>
								</Container>
							</Flex>
						</Box>
					</Flex>

					{comment.viewerCanUpdate && !active && (
						<Flex display="flex" alignSelf="end" pb={1}>
							<Flex flexGrow={0} flexShrink={0}>
								{commentArchiveResponse.loading ? (
									<Spinner />
								) : (
									<StyledBox>
										{button}
										<StyledButton
											icon={<JunkIcon />}
											type="destructive"
											onClick={() => onArchiveClick()}
										/>
									</StyledBox>
								)}
							</Flex>
						</Flex>
					)}
				</Flex>
			)}
		</Editable.Quill.Button>
	);
}

const Container = styled(Box)`
	border-radius: 8px;
	background: ${(props) => props.theme.colors.whiteBlue};
`;

const StyledBox = styled(Box)`
	display: flex;
	gap: 5px;
	div {
		margin-left: 0;

		div {
			padding: 2.5px;
		}
	}
`;

const StyledButton = styled(Button)`
	padding: 2.5px;
	margin: 0;
`;

Comment.Skeleton = CommentSkeleton;
